/* eslint-disable jsx-a11y/anchor-is-valid */
import { PartnershipOfferFeeType, Product, PartnershipOfferUrl } from '@axl-dev/school-sdk';
import { Tooltip } from 'antd';
import { FC, useCallback, useState } from 'react';

import { SortingDirection } from '@app/shared/lib/enums/sorting-direction';
import { useSchool, useSdk } from '@app/shared/lib/hooks';
import { Copyable, HelpText, Loc, Price, Table, ValuePlaceholder } from '@app/shared/ui';
import { TruncateItems } from '@app/shared/ui/TruncateItems';

const PartnershipFeeValue: React.FC<{
    value: number;
    type: PartnershipOfferFeeType;
}> = ({ value, type }) => {
    return type === 'percent' ? <>{value}%</> : <Price price={value} />;
};

interface OfferTableProps {
    search: string;
}
export const OfferTable: FC<OfferTableProps> = ({ search }) => {
    const school = useSchool();
    const sdk = useSdk();
    const [isEmpty, setEmpty] = useState(false);

    const fetchItemsByFilter = useCallback(
        async (filters: {
            pageTotal: number;
            itemsTotal: number;
            take: number;
            page: number;
            sortName?: string | undefined;
            sortType: SortingDirection;
        }) => {
            const res = await sdk.partnership.offerStore.fetch({
                ...filters,
                search: search || "",
                sortType: filters.sortType === SortingDirection.asc ? 'Asc' : 'Desc'
            });

            if (res.body.items.length === 0) {
                setEmpty(true);
            } else {
              setEmpty(false)
            }

            return {
                items: res.body.items,
                filters: {
                    ...res.body.filter,
                    search: search || "",
                    sortType: res.body.filter.sortType === 'Desc' ? SortingDirection.desc : SortingDirection.asc,
                    pageTotal: sdk.partnership.offerStore.filter.pageTotal,
                    page: sdk.partnership.offerStore.filter.page
                }
            };
        },
        [search]
    );

    return (
        <>
            {isEmpty && (
              <div className='flex mt-12'>
                <HelpText
                    label={<Loc word="Partnership.offer.empty" default="No offers available" />}
                    className="fs-14"
                />
                </div>
            )}

            <div className='mt-12' style={{ display: isEmpty ? 'none' : 'block' }}>
                <Table
                    defaultFilters={{ take: 25 }}
                    fetchItemsByFilter={fetchItemsByFilter}
                    rowBordered
                    columns={[
                        {
                            title: <Loc word="Partnership.offer.title" default="Title and description" />,
                            key: 'name',
                            width: '30%',
                            render: (_text, item) => (
                                <>
                                    <ValuePlaceholder value={item.title} className="line-clamp-1" />
                                    <div className="break-word text-placeholder">{item.description}</div>
                                </>
                            )
                        },
                        {
                            title: <Loc word="Partnership.offer.fee" default="Fee" />,
                            key: 'fee',
                            width: '20%',
                            align: 'right',
                            render: (_text, item) => {
                                if (school.partnership?.isSingleLevel === true)
                                    return <PartnershipFeeValue value={item.levels[0] ?? 0} type={item.feeType} />;

                                const levels = Array.from({ length: school.partnership?.levelCount ?? 1 }, () => 0).map(
                                    (_, i) => item.levels[i] ?? 0
                                );

                                return (
                                    <Tooltip
                                        title={
                                            <div>
                                                {levels.map((x, i) => (
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    <div key={i}>
                                                        <Loc
                                                            word="Partnership.levelFeeTemplate"
                                                            default="Fee level {0}"
                                                        >
                                                            {i + 1}
                                                        </Loc>
                                                        :&nbsp;
                                                        <PartnershipFeeValue value={x} type={item.feeType} />
                                                    </div>
                                                ))}
                                            </div>
                                        }
                                        trigger="click"
                                    >
                                        <a>
                                            <Loc word="Partnership.levelFeeTemplate" default="Fee level {0}">
                                                {1}
                                            </Loc>
                                            :&nbsp;
                                            <PartnershipFeeValue value={item.levels[0] ?? 0} type={item.feeType} />
                                        </a>
                                    </Tooltip>
                                );
                            }
                        },
                        {
                            title: <Loc word="Partnership.offer.products" default="Products" />,
                            key: 'products',
                            width: '25%',
                            render: (_text, item) => (
                                <div className="lh-18">
                                    {item.productType === 'all' ? (
                                        <Loc word="Partnership.offer.productType.all" default="All products" />
                                    ) : (
                                        <TruncateItems<Product> items={item.products} direction="vertical">
                                            {x => (
                                                <ValuePlaceholder value={x.name} type="untitled" className="d-block" />
                                            )}
                                        </TruncateItems>
                                    )}
                                </div>
                            )
                        },
                        {
                            title: <Loc word="Partnership.offer.urls" default="Links for promotion" />,
                            key: 'url',
                            width: '25%',
                            render: (_text, item) => (
                                <TruncateItems<PartnershipOfferUrl>
                                    items={item.urls}
                                    direction="vertical"
                                    spaceSize="small"
                                >
                                    {partnershipUrl => (
                                        <div style={{ maxWidth: 400 }}>
                                            <div className="fs-10 text-gray-6">{partnershipUrl.name}</div>
                                            <div className="flex align-center gap-5">
                                                <Copyable text={partnershipUrl.url} />
                                                <div className="ellipsis">
                                                    <a href={partnershipUrl.url} target="_blank" rel="noreferrer">
                                                        {partnershipUrl.url}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </TruncateItems>
                            )
                        }
                    ]}
                    hidePaginationOnePage
                    size="small"
                    style={{ maxWidth: 1280 }}
                />
            </div>
        </>
    );
};
